import type { LevelSnapshot } from "@gridflow/core";

export interface StuckDetectorOptions {
  readonly firstEligibleLevelOrder?: number;
  readonly thresholdMs?: number;
  readonly forceAvailable?: boolean;
  readonly allowPureTimeHint?: boolean;
}

export interface StuckDetectorSnapshot {
  readonly elapsedSinceActionMs: number;
  readonly hasIssueSignal: boolean;
  readonly isHintEligibleLevel: boolean;
  readonly isHintAvailable: boolean;
  readonly availabilityReason: "forced" | "issue-signal" | "idle-time" | null;
  readonly progressRatio: number;
  readonly remainingMs: number;
}

export class StuckDetector {
  private elapsedSinceActionMs = 0;
  private hasIssueSignal = false;

  public constructor(
    private readonly levelOrder: number,
    private readonly options: StuckDetectorOptions = {}
  ) {}

  public advance(deltaMs: number): void {
    if (!Number.isFinite(deltaMs) || deltaMs <= 0) {
      return;
    }
    this.elapsedSinceActionMs += deltaMs;
  }

  public recordEffectiveAction(): void {
    this.elapsedSinceActionMs = 0;
  }

  public recordClear(): void {
    this.hasIssueSignal = true;
  }

  public recordLengthIssue(snapshot: LevelSnapshot): void {
    if (snapshot.paths.some((path) => path.currentLength > path.targetLength)) {
      this.hasIssueSignal = true;
      return;
    }
    if (snapshot.paths.some((path) => path.isLinked && !path.isComplete)) {
      this.hasIssueSignal = true;
    }
  }

  public consumeHint(): void {
    this.elapsedSinceActionMs = 0;
    this.hasIssueSignal = false;
  }

  public getSnapshot(): StuckDetectorSnapshot {
    const isHintEligibleLevel = this.levelOrder >= this.firstEligibleLevelOrder;
    const availabilityReason = this.getAvailabilityReason(isHintEligibleLevel);
    const progressRatio = isHintEligibleLevel
      ? Math.max(0, Math.min(1, this.elapsedSinceActionMs / this.thresholdMs))
      : 0;
    return {
      elapsedSinceActionMs: Math.round(this.elapsedSinceActionMs),
      hasIssueSignal: this.hasIssueSignal,
      isHintEligibleLevel,
      isHintAvailable: availabilityReason !== null,
      availabilityReason,
      progressRatio,
      remainingMs: Math.max(0, Math.round(this.thresholdMs - this.elapsedSinceActionMs))
    };
  }

  private getAvailabilityReason(
    isHintEligibleLevel: boolean
  ): StuckDetectorSnapshot["availabilityReason"] {
    if (!isHintEligibleLevel) {
      return null;
    }
    if (this.options.forceAvailable === true) {
      return "forced";
    }
    if (this.elapsedSinceActionMs < this.thresholdMs) {
      return null;
    }
    if (this.hasIssueSignal) {
      return "issue-signal";
    }
    if (this.options.allowPureTimeHint ?? true) {
      return "idle-time";
    }
    return null;
  }

  private get firstEligibleLevelOrder(): number {
    return this.options.firstEligibleLevelOrder ?? 6;
  }

  private get thresholdMs(): number {
    return this.options.thresholdMs ?? 90_000;
  }
}

export function createStuckDetector(
  levelOrder: number,
  options?: StuckDetectorOptions
): StuckDetector {
  return new StuckDetector(levelOrder, options);
}
